import { CompleteServiceData } from './serviceData';

// Re-export the interface for convenience
export type { CompleteServiceData } from './serviceData';

// Cloud Cost Optimization & FinOps Consulting Services
const cloudCostOptimizationService: CompleteServiceData = {
  hero: {
    icon: '💰',
    category: 'FinOps',
    title: 'Cloud Cost Optimization & FinOps Consulting Services',
    subtitle: 'Maximize Your Cloud ROI — Without Sacrificing Performance',
    description: 'Reduce your AWS, GCP, Azure, and Kubernetes costs with our expert-led FinOps strategies.',
    highlights: [
      '30–60% Cost Reduction',
      'Platform Agnostic',
      'Compliance-Safe',
      'Real-time monitoring & governance'
    ],
    ctaButtons: {
      primary: {
        text: 'Schedule a Free Audit',
        link: '/contact'
      },
      secondary: {
        text: 'Learn More',
        link: '/case-studies'
      }
    }
  },
  content: {
    sections: [
      {
        title: 'Core FinOps Solutions',
        type: 'cards',
        content: [
          {
            title: '💰 Control Cloud Costs Without Sacrificing Performance',
            description: 'Identify and eliminate idle compute, overprovisioned storage, and non-critical workloads. Implement auto-scaling, rightsizing, and spot/preemptible usage to reduce cloud spend.',
            features: [
              'Auto-scaling and rightsizing strategies',
              'Spot/preemptible instance optimization',
              'Resource utilization monitoring'
            ]
          },
          {
            title: '📡 Usage Analytics & Visibility',
            description: 'Build dashboards using Prometheus, Grafana, or native billing APIs to map cost to environments, services, and teams. Enable real-time visibility across multi-cloud usage.',
            features: [
              'Real-time cost visibility',
              'Multi-cloud usage tracking',
              'Team-based cost attribution'
            ]
          },
          {
            title: '🧩 Architecture-Aware Cost Efficiency',
            description: 'Redesign infrastructure for cost awareness: shift from monoliths to autoscaled microservices, optimize networking and storage classes based on traffic and data patterns.',
            features: [
              'Network optimization',
              'Storage class optimization',
              'Traffic pattern analysis'
            ]
          }
        ]
      },
      {
        title: 'Advanced Governance & Controls',
        type: 'cards',
        content: [
          {
            title: '🔐 Team & Access-Based Governance',
            description: 'Set up tagging policies, IAM access boundaries, and service quotas to attribute cost by team and prevent shadow IT. Use automation to enforce policy compliance.',
            features: [
              'Automated policy compliance',
              'Shadow IT prevention',
              'Cost ownership assignment'
            ]
          },
          {
            title: '🧠 Intelligent Budget Controls',
            description: 'Define team-wise budgets and integrate alerting via Slack, Opsgenie, or PagerDuty when thresholds are breached. Automate scaling decisions based on forecasted load.',
            features: [
              'Threshold breach alerts',
              'Automated scaling decisions',
              'Forecasted load management'
            ]
          },
          {
            title: '📥 Kubernetes Cost Breakdown',
            description: 'Break down Kubernetes costs by namespace, pod, or workload using Kubecost/OpenCost. Implement node pool segmentation, spot instance pools, and right-sizing policies for containerized workloads.',
            features: [
              'Node pool segmentation',
              'Spot instance pools',
              'Container rightsizing policies'
            ]
          }
        ]
      },
      {
        title: 'What Do We Offer in Cloud FinOps Consulting?',
        type: 'benefits',
        content: [
          {
            icon: '🌐',
            title: 'FinOps Strategy Advisory',
            description: 'We help you align your cloud investment with your growth strategy — not just reduce your bill.',
            features: [
              'Cost-first mindset establishment',
              'Cross-functional alignment',
              'Measurable business outcomes'
            ]
          },
          {
            icon: '⚙️',
            title: 'Intelligent Cost Monitoring & Automation',
            description: 'Manual cost tracking is slow, reactive, and error-prone. Our team implements intelligent automation.',
            features: [
              'Real-time anomaly detection',
              'Predictive cost thresholds',
              'Automated remediation actions'
            ]
          },
          {
            icon: '🔐',
            title: 'Cloud Governance That Balances Cost, Security, and Control',
            description: 'Bring structure, compliance, and transparency across your cloud environments.',
            features: [
              'Policy-as-code practices',
              'Automated compliance enforcement',
              'Full cost attribution'
            ]
          },
          {
            icon: '📦',
            title: 'Migration Cost Assessment',
            description: 'Get a clear financial and operational view before making migration decisions.',
            features: [
              'Total cost of ownership analysis',
              'Hidden cost driver identification',
              'ROI protection strategies'
            ]
          }
        ]
      },
      {
        title: 'Why Partner with Us for FinOps Consulting?',
        type: 'benefits',
        content: [
          {
            icon: '🧠',
            title: 'Deep Engineering Expertise',
            description: 'Seasoned DevOps and SRE professionals with architecture-level thinking.',
            features: [
              'Multi-cloud platform expertise',
              'Container orchestration mastery',
              'Infrastructure optimization'
            ]
          },
          {
            icon: '⚡',
            title: 'Serverless & Event-Driven Architecture Tuning',
            description: 'Optimize Lambda, Cloud Functions, or Step Functions for cost and performance.',
            features: [
              'Concurrency optimization',
              'Cold-start reduction',
              'Memory sizing tuning'
            ]
          },
          {
            icon: '🗃️',
            title: 'Database Cost Optimization',
            description: 'Analyze database workloads across RDS, Aurora, CloudSQL, MongoDB, and Elasticsearch.',
            features: [
              'Unused replica elimination',
              'IOPS waste reduction',
              'Instance class optimization'
            ]
          }
        ]
      },
      {
        title: 'Supported Tools & Technologies',
        type: 'tools',
        tools: [
          {
            category: 'Cloud Platforms',
            items: ['AWS', 'Google Cloud Platform', 'Microsoft Azure', 'Kubernetes', 'Heroku'],
             description: 'Our team specializes in deploying and managing applications across major cloud platforms. We help organizations leverage the full potential of cloud infrastructure for maximum scalability, reliability, and cost-efficiency.',
            icon: '☁️'
          },
          {
            category: 'Cost Monitoring',
            items: ['Kubecost', 'OpenCost', 'Prometheus', 'Grafana', 'Cloud Billing APIs'],
            description: 'Take control of your cloud spending with our comprehensive cost monitoring solutions. We provide real-time visibility into resource usage and spending patterns to help optimize your cloud investments.',
            icon: '💰'
          },
          {
            category: 'Automation',
            items: ['Terraform', 'Ansible', 'AWS Lambda', 'Cloud Functions', 'Auto-scaling Groups'],
            description: 'Streamline your operations with intelligent automation solutions. Our automation expertise helps eliminate repetitive tasks, reduce human error, and accelerate development and deployment processes.',
            icon: '⚙️'
          },
          {
            category: 'Alerting & Notification',
            items: ['Slack', 'PagerDuty', 'Opsgenie', 'AWS SNS', 'Google Cloud Pub/Sub'],
            description: 'Stay ahead of potential issues with our robust alerting and notification systems. We implement comprehensive monitoring to ensure you\'re immediately informed of any critical events requiring attention.',
            icon: '🔔'
          }
        ]
      },
      {
        title: 'Getting Started with Prometheus & Grafana Stack',
        type: 'process',
        content: [
          {
            icon: '🔍',
            title: 'Initial Monitoring Assessment',
            description: 'Scoping workshop to identify telemetry gaps, exporters, and critical SLOs/SLA targets.',
            features: [
              'Catalog existing exporters',
              'Review metric retention requirements',
              'Identify critical SLOs and SLAs'
            ]
          },
          {
            icon: '🧪',
            title: 'Proof of Concept (PoC) Deployment',
            description: 'Deploy minimal Prometheus + Grafana stack in sandbox to validate data flow and alerts.',
            features: [
              'Sandbox environment setup',
              'Sample workload onboarding',
              'Basic alert rules configuration'
            ]
          },
          {
            icon: '🚀',
            title: 'Full-Scale Implementation',
            description: 'Production rollout with Terraform/Helm, federated servers, Cortex/Thanos backends, and RBAC.',
            features: [
              'Federated Prometheus servers',
              'Cortex/Thanos backend deployment',
              'Grafana dashboards with RBAC'
            ]
          },
          {
            icon: '⚙️',
            title: 'Alerting & Dashboard Tuning',
            description: 'Refine alert rules, design SLO dashboards, and optimize performance through load testing.',
            features: [
              'Recording rule refinement',
              'Precise alert expressions',
              'Load testing and scrape interval optimization'
            ]
          },
          {
            icon: '🎓',
            title: 'Operational Handoff & Training',
            description: 'Provide runbooks and conduct workshops for SREs on maintenance and troubleshooting.',
            features: [
              'Runbooks for TSDB and Grafana maintenance',
              'Hands-on debug workflow training',
              'Extending dashboards for new services'
            ]
          },
          {
            icon: '♻️',
            title: 'Ongoing Support & Optimization',
            description: '24/7 incident support, periodic health checks, and architecture reviews for scaling and cost.',
            features: [
              '24/7 critical incident support',
              'TSDB growth and scrape health monitoring',
              'Quarterly reviews and scaling advice'
            ]
          }
        ]
      }      
    ]
  },
  industryUseCases: {
    title: 'Industries & Use Cases We Specialize In',
    subtitle: 'We bring domain-aware cost optimization strategies tailored to your cloud workloads, business models, and team structures.',
    useCases: [
      {
        icon: '🏢',
        title: 'SaaS Platforms',
        description: 'Optimize per-tenant cost in multi-tenant systems, implement per-service cost breakdown, and enable FinOps reporting for engineering and finance.',
        features: [
          'Multi-tenant cost optimization',
          'Per-service cost breakdown',
          'Engineering and finance alignment'
        ]
      },
      {
        icon: '🧬',
        title: 'ML & AI Workloads',
        description: 'Reduce GPU idle time, auto-scale training workloads, and optimize storage tiers for model checkpoints, datasets, and logs.',
        features: [
          'GPU idle time reduction',
          'Training workload auto-scaling',
          'Model storage optimization'
        ]
      },
      {
        icon: '🛒',
        title: 'E-commerce & Retail',
        description: 'Implement caching strategies, edge optimization, and auto-scaling APIs for sales peaks (e.g., during promotions), while minimizing infrastructure bloat off-season.',
        features: [
          'Peak traffic optimization',
          'Seasonal scaling strategies',
          'Edge caching implementation'
        ]
      },
      {
        icon: '🩺',
        title: 'HealthTech & Regulated Industries',
        description: 'Align with HIPAA/SOC2 budgets and enforce strict cost visibility, network isolation, and access control in cloud-native stacks.',
        features: [
          'HIPAA/SOC2 compliance',
          'Network isolation',
          'Audit-ready cost controls'
        ]
      },
      {
        icon: '🎮',
        title: 'High-Traffic Event Systems',
        description: 'Implement short-lived infrastructure patterns, real-time autoscaling, and anomaly-based alerting to manage burst workloads under budget.',
        features: [
          'Burst workload management',
          'Real-time autoscaling',
          'Anomaly-based alerting'
        ]
      },
      {
        icon: '🧪',
        title: 'Dev/Test Environments',
        description: 'Automate spin-up/spin-down of sandbox environments, enforce TTL policies, and isolate billing across teams and projects.',
        features: [
          'Environment automation',
          'TTL policy enforcement',
          'Team-based billing isolation'
        ]
      }
    ]
  },
  additionalSections: [
    {
      type: 'rich-text',
      title: '🔍 Struggling with Rising Cloud Costs? We Help You Take Control',
      content: `If you're spending more on cloud infrastructure than you'd planned — and still don't have clear answers about what's driving that spend — you're not alone. Most fast-growing companies reach a point where cloud bills become unpredictable, cost accountability is unclear, and engineering teams are forced to choose between speed and efficiency. That's exactly where we come in.

Our FinOps Strategy Services are designed to help you turn cloud spending from a liability into a lever for scale. We help you build a cloud cost operating model that gives your engineering and finance teams shared visibility, shared responsibility, and the power to act — without compromising on agility, innovation, or uptime.

We start by understanding your cloud usage patterns, business goals, and technical architecture. From there, we design a cost optimization framework tailored to your environment — whether you're running on AWS, GCP, Azure, or Kubernetes.

✔ Real-time cost visibility across multi-cloud environments
✔ Team-level cost ownership and attribution
✔ Automated governance and policy enforcement
✔ Developer-integrated cost awareness tools

Your developers won't be left behind. We integrate cost awareness directly into your CI/CD pipelines, monitoring dashboards, and developer workflows. Your teams gain visibility into what they deploy, how much it costs, and how to make smarter decisions — without slowing them down or adding bureaucracy.`
    }
  ],
  faq: {
    title: 'Frequently Asked Questions',
    faqs: [
      {
        question: 'How much can we realistically save on cloud costs?',
        answer: 'Most of our clients see 30-60% cost reduction within the first 6 months. The exact savings depend on your current infrastructure efficiency, workload patterns, and willingness to adopt cost-optimized architectures.'
      },
      {
        question: 'Will cost optimization impact our application performance?',
        answer: 'No. Our approach focuses on eliminating waste and inefficiencies while maintaining or improving performance. We use techniques like rightsizing, auto-scaling, and intelligent resource allocation that actually enhance performance characteristics.'
      },
      {
        question: 'How long does it take to implement FinOps strategies?',
        answer: 'Initial quick wins can be achieved within 2-4 weeks. A comprehensive FinOps implementation typically takes 3-6 months, depending on your infrastructure complexity and organizational readiness.'
      },
      {
        question: 'Do you support multi-cloud environments?',
        answer: 'Yes, we specialize in multi-cloud cost optimization across AWS, GCP, Azure, and hybrid environments. Our platform-agnostic approach ensures consistent cost management regardless of your cloud provider mix.'
      }
    ]
  }
};

// Kubernetes Consulting Services
const kubernetesConsultingService: CompleteServiceData = {
  hero: {
    icon: '⚙️',
    category: 'Infrastructure',
    title: 'Kubernetes Consulting Services',
    subtitle: 'Enterprise Kubernetes Architecture & Implementation',
    description: 'Leverage our deep expertise in Kubernetes architecture, automation, and security for robust container platforms that drive agility, scalability, and resilience.',
    highlights: [
      'Proven Enterprise Experience',
      'Automation-First Philosophy',
      'Security & Compliance Built In',
      'Cloud-Agnostic Expertise'
    ]
  },
  content: {
    sections: [
      {
        title: 'Why Betaque for Kubernetes Consulting?',
        type: 'benefits',
        content: [
          {
            icon: '🏆',
            title: 'Proven Enterprise Experience',
            description: 'We\'ve architected and managed Kubernetes environments for high-scale, 3000+ microservice landscapes, handling 10million+ API hits/month.',
            features: [
              'High-scale deployments',
              '3000+ microservices managed',
              'Real-world demand handling'
            ]
          },
          {
            icon: '🤖',
            title: 'Automation-First Philosophy',
            description: 'Betaque delivers prebuilt Helm charts, Terraform modules, and GitOps pipelines that eliminate manual toil.',
            features: [
              'Prebuilt Helm charts',
              'Terraform modules',
              'GitOps pipelines'
            ]
          },
          {
            icon: '🔒',
            title: 'Security & Compliance Built In',
            description: 'From CIS-hardened node images to automated Admission Controller policies, we embed security controls at every layer.',
            features: [
              'CIS-hardened node images',
              'Automated policies',
              'SOC 2 and PCI-DSS guidance'
            ]
          },
          {
            icon: '☁️',
            title: 'Cloud-Agnostic Expertise (BYOC Enabled)',
            description: 'Whether you prefer AWS EKS, GCP GKE, Azure AKS, or on-prem clusters, our BYOC approach lets you retain account ownership.',
            features: [
              'Multi-cloud support',
              'Account ownership retention',
              'Consistent tooling across providers'
            ]
          }
        ]
      },
      {
        title: 'Why Partner with Us for Kubernetes Consulting?',
        type: 'benefits',
        content: [
          {
            icon: '🚀',
            title: 'Accelerated Time-to-Value',
            description: 'Deploy production-grade clusters quickly using prebuilt modules, GitOps pipelines, and runbooks.',
            features: [
              'Prebuilt modules for rapid setup',
              'GitOps pipelines integration',
              'Well-defined runbooks'
            ]
          },
          {
            icon: '🛡️',
            title: 'Resilient, Production-Grade Architecture',
            description: 'Ensure SLA adherence through high availability and automated recovery mechanisms.',
            features: [
              'HA control planes',
              'Zero-trust networking',
              'Automated recovery workflows'
            ]
          },
          {
            icon: '💰',
            title: 'Cost-Efficient Operations',
            description: 'Optimize cloud costs with intelligent resource management strategies.',
            features: [
              'Rightsizing workloads',
              'Spot instance usage',
              'Continuous drift detection'
            ]
          },
          {
            icon: '🔐',
            title: 'Built-In Security & Compliance',
            description: 'Security and compliance are embedded into the lifecycle with policy-driven controls.',
            features: [
              'CIS-hardening practices',
              'Policy-as-code checks',
              'Automated audit logging'
            ]
          },
          {
            icon: '🌐',
            title: 'Future-Proof Scale',
            description: 'Leverage modular infrastructure and GitOps to scale across any environment.',
            features: [
              'Modular, declarative infrastructure',
              'GitOps-driven scaling',
              'Support for public, hybrid, and edge cloud'
            ]
          }
        ]
      },    
      {
        title: 'Core Kubernetes Consulting Offerings',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Cluster Assessment & Proof of Concept',
            description: 'Deep dive into your existing compute resources, networking, and workload topology with prioritized action plans and PoC implementation.'
          },
          {
            step: 2,
            title: 'Architecture Design & Deployment',
            description: 'High-availability cluster architecture with multi-AZ control planes, GitOps-powered CI/CD pipelines, and network topology design.'
          },
          {
            step: 3,
            title: 'Migration & Modernization',
            description: 'VM/VMware to Kubernetes migration, on-prem to cloud-managed Kubernetes, and service mesh integration.'
          },
          {
            step: 4,
            title: 'Managed Kubernetes & Day-2 Operations',
            description: '24/7 cluster health monitoring, automated patching, backup & disaster recovery, and cost optimization.'
          },
          {
            step: 5,
            title: 'Training & Knowledge Transfer',
            description: 'Hands-on workshops, custom runbooks, and ongoing DevOps mentorship for autonomous cluster management.'
          }
        ]
      },
      {
        title: 'Multi-Regional and Multi-Zonal Architecture Expertise',
        type: 'cards',
        content: [
          {
            title: 'Distributed Control Plane & Data Plane',
            description: 'Highly available Kubernetes environments with distributed control planes across three or more availability zones.',
            features: [
              'Multi-zonal control planes',
              'Automated etcd clustering',
              'Cross-region replication'
            ]
          },
          {
            title: 'Global Load Balancing and Traffic Steering',
            description: 'DNS-based global load balancers and Anycast ingress solutions for nearest healthy region routing.',
            features: [
              'Global load balancers',
              'Health-check probes',
              'Rapid failover capabilities'
            ]
          },
          {
            title: 'Data Locality and Stateful Workloads',
            description: 'Multi-regional storage replication and persistent volume claims bound to regional storage classes.',
            features: [
              'Cross-region storage replication',
              'Regional storage classes',
              'Application-level caching'
            ]
          }
        ]
      }
    ]
  },
  faq: {
    title: 'Kubernetes Consulting FAQ',
    faqs: [
      {
        question: 'How long does it take to deploy a production-grade Kubernetes cluster?',
        answer: 'With our prebuilt modules and GitOps pipelines, you can deploy production-grade clusters in days—not weeks. The timeline depends on your specific requirements, but we accelerate time-to-value significantly.'
      },
      {
        question: 'Do you support multi-cloud Kubernetes deployments?',
        answer: 'Yes, we specialize in cloud-agnostic Kubernetes deployments across AWS EKS, GCP GKE, Azure AKS, and on-premise clusters with consistent tooling and best practices.'
      },
      {
        question: 'What makes your Kubernetes consulting different?',
        answer: 'Our automation-first philosophy, proven enterprise experience with 3000+ microservices, and security-by-design approach set us apart. We don\'t just deploy clusters—we build resilient, cost-effective platforms.'
      }
    ]
  }
};

// Terraform and Pulumi Consulting
const terraformPulumiService: CompleteServiceData = {
  hero: {
    icon: '🏗️',
    category: 'Infrastructure',
    title: 'Terraform and Pulumi Consulting',
    subtitle: 'Infrastructure-as-Code Excellence',
    description: 'Automate and scale your cloud infrastructure with battle-tested IaC practices using Terraform and Pulumi as core engines.',
    highlights: ['Modular Templates', 'Multi-Cloud Expertise', 'CI/CD Integration', 'Security Built-In']
  },
  content: {
    sections: [
      {
        title: 'Why Betaque for IaC with Terraform & Pulumi?',
        type: 'benefits',
        content: [
          {
            icon: '📦',
            title: 'Modular, Prebuilt Terraform Templates & Terragrunt Best Practices',
            description: 'We maintain a library of reusable Terraform modules encapsulating networking, compute, storage, and security patterns.',
            features: ['Reusable modules', 'DRY principles', 'Cross-account workflows']
          },
          {
            icon: '☁️',
            title: 'Multi-Cloud & Hybrid Expertise',
            description: 'Create cross-platform Pulumi stacks and Terraform configurations that map services while preserving feature parity.',
            features: ['AWS, GCP, Azure support', 'Service parity mapping', 'Hybrid cloud ready']
          },
          {
            icon: '🔄',
            title: 'End-to-End CI/CD Integration',
            description: 'Terraform plans and applies directly tied to pull requests with automated peer review and version control.',
            features: ['Pull request automation', 'Peer review enforced', 'Audit trails']
          }
        ]
      },
      {
        title: 'Why Partner with Us for IaC Consulting?',
        type: 'benefits',
        content: [
          {
            icon: '⚙️',
            title: 'Rapid, Consistent Environment Provisioning',
            description: 'Provision identical dev, staging, and prod environments in minutes using reusable modules and automation tools.',
            features: [
              'Terraform and Pulumi module standardization',
              'Terragrunt-based multi-environment reuse',
              'Reduced human error through automation'
            ]
          },
          {
            icon: '🔄',
            title: 'Pull-Request-Driven Infrastructure Delivery',
            description: 'Peer-reviewed, auditable infrastructure updates triggered by pull requests ensure safe and trackable changes.',
            features: [
              'Automated terraform plan & apply via PRs',
              'CI/CD integrations with Atlantis, TFC, or GitHub Actions',
              'Audit trails for every change'
            ]
          },
          {
            icon: '🧭',
            title: 'Continuous Drift Detection & Automated Remediation',
            description: 'Monitor and correct any deviation from declared IaC state to keep cloud environments aligned.',
            features: [
              'Scheduled drift checks',
              'Alerts for configuration mismatch',
              'Automated state remediation pipelines'
            ]
          },
          {
            icon: '🔐',
            title: 'Built-In Security & Compliance',
            description: 'Policy-as-code and encrypted secrets management ensure compliance and secure deployments.',
            features: [
              'Sentinel or Checkov for policy-as-code',
              'Least-privilege IAM embedded in modules',
              'Vault/KMS integration for secrets'
            ]
          },
          {
            icon: '🧱',
            title: 'Scalable, Maintainable Codebases',
            description: 'Modular design enables seamless updates and low maintenance across evolving infrastructure.',
            features: [
              'Version-controlled reusable modules',
              'Support for new cloud features via single-module updates',
              'Reduced technical debt'
            ]
          },
          {
            icon: '📥',
            title: 'Importing Existing Infrastructure into IaC',
            description: 'Easily onboard unmanaged cloud resources into IaC without disruption using automation.',
            features: [
              'Automated scanning of AWS/GCP/Azure',
              'Skeleton code generation for existing resources',
              'Safe IaC onboarding for live systems'
            ]
          }
        ]
      }      
    ]
  }
};

// SRE Consulting Services
const sreConsultingService: CompleteServiceData = {
  hero: {
    icon: '🔧',
    category: 'Reliability',
    title: 'SRE Consulting Services',
    subtitle: 'Site Reliability Engineering Built for Scale, Speed, and Stability',
    description: 'Build reliable, observable, and scalable systems using modern SRE practices that blend engineering, automation, and DevOps principles.',
    highlights: ['Production Readiness', 'Observability Stack', 'Incident Management', 'Release Engineering']
  },
  content: {
    sections: [
      {
        title: 'What We Offer',
        type: 'cards',
        content: [
          {
            title: 'Production Readiness & Resilience Engineering',
            description: 'Design high-availability and failover strategies across regions using Kubernetes, service mesh, and SLO-driven frameworks.',
            features: ['Multi-region HA', 'SLO frameworks', 'Failover strategies']
          },
          {
            title: 'Observability Architecture',
            description: 'Full-stack observability using Prometheus, Grafana, ELK, OpenTelemetry, and custom dashboards.',
            features: ['Metrics and traces', 'Golden signal alerting', 'Faster MTTR']
          },
          {
            title: 'Incident Management & Automation',
            description: 'Streamline incident response with auto-alert routing, runbooks, and RCA frameworks.',
            features: ['Auto-alert routing', 'Runbook automation', 'RCA templates']
          }
        ]
      },
      {
        title: 'Why Partner with Us for SRE Consulting?',
        type: 'benefits',
        content: [
          {
            icon: '🌐',
            title: 'Proven Scale & Performance Expertise',
            description: 'We support platforms handling billions of API calls and millions of users with production-tested strategies.',
            features: [
              'Experience with 40B+ API hits/month',
              'Support for 100M+ users and petabyte-scale systems',
              'Production-hardened solutions'
            ]
          },
          {
            icon: '🧩',
            title: 'Full-Stack SRE Capabilities',
            description: 'From infrastructure reliability to user-facing performance, we provide complete SRE coverage.',
            features: [
              'Infrastructure-to-UX observability',
              'End-to-end performance optimization',
              'On-call reliability improvements'
            ]
          },
          {
            icon: '☁️',
            title: 'Cloud-Native, Cross-Platform Architecture',
            description: 'Design and optimize SRE systems across clouds using container-native best practices.',
            features: [
              'Cross-cloud SRE patterns',
              'Container-native monitoring and resilience',
              'Seamless multi-environment support'
            ]
          },
          {
            icon: '📊',
            title: 'SLO Tracking & Intelligent Dashboards',
            description: 'Automate error budget tracking and observability via real-time dashboards and alerting systems.',
            features: [
              'Automated SLO/SLA monitoring',
              'Custom SRE dashboards',
              'Real-time error budget analytics'
            ]
          },
          {
            icon: '🛠️',
            title: 'Chaos Engineering & Debugging Playbooks',
            description: 'Equip your teams with structured incident response and resilience engineering toolkits.',
            features: [
              'Ready-to-use debugging playbooks',
              'Chaos engineering framework integration',
              'On-call fatigue reduction strategies'
            ]
          }
        ]
      },
      {
        title: 'Supported Tools & Ecosystem',
        type: 'tools',
        tools: [
          {
            category: 'Observability & Monitoring',
            items: ['Prometheus', 'Grafana', 'OpenTelemetry', 'Loki', 'Jaeger', 'ELK Stack', 'Datadog', 'CloudWatch'],
            description: 'Gain deep visibility into your systems with our advanced observability stack. We help set up monitoring, tracing, and logging to ensure system health, fast debugging, and improved reliability.',
            icon: '📈'
          },
          {
            category: 'Deployment & GitOps',
            items: ['ArgoCD', 'Argo Rollouts', 'FluxCD', 'Helm', 'Kustomize', 'Skaffold', 'Spinnaker'],
            description: 'Empower your delivery pipeline with GitOps and declarative deployments. We automate complex rollouts, simplify configurations, and ensure repeatable, version-controlled deployments.',
            icon: '🚀'
          },
          {
            category: 'CI/CD Pipelines',
            items: ['GitHub Actions', 'GitLab CI/CD', 'Jenkins', 'CircleCI', 'Drone CI', 'Bitbucket Pipelines'],
            description: 'Accelerate your development lifecycle with modern CI/CD pipelines. We build fast, secure, and scalable delivery workflows tailored to your engineering needs.',
            icon: '🔁'
          },
          {
            category: 'Infrastructure as Code & Automation',
            items: ['Terraform', 'Terragrunt', 'Atlantis', 'AWS CDK', 'Azure Bicep'],
            description: 'Define, manage, and automate your infrastructure at scale. Our IaC expertise ensures consistency, traceability, and efficiency across cloud environments.',
            icon: '📦'
          },
          {
            category: 'Incident Management',
            items: ['PagerDuty', 'OpsGenie', 'FireHydrant', 'Statuspage', 'Slack integrations'],
            description: 'Ensure rapid incident response with integrated management tools. We help set up alert routing, on-call rotations, and communication workflows to minimize downtime.',
            icon: '🚨'
          },
          {
            category: 'Reliability Tooling',
            items: ['KEDA', 'HPA', 'VPA', 'Chaos Mesh', 'LitmusChaos', 'kube-bench', 'kube-hunter'],
            description: 'Boost system resilience with proactive reliability tools. From auto-scaling to chaos engineering and security scanning, we prepare your stack for real-world failures.',
            icon: '🛡️'
          },
          {
            category: 'Secrets & Config Management',
            items: ['HashiCorp Vault', 'External Secrets', 'AWS Secret Manager', 'GCP Secret Manager'],
            description: 'Secure and streamline your configuration and secrets management. We implement solutions to safely handle sensitive data across all environments.',
            icon: '🔐'
          }
        ]
      },
      {
        title: 'Action Plan: Our SRE Engagement Process',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Platform Assessment & SLO Definition',
            description: 'Analyze current reliability posture, incidents, and toil; define SLIs, SLOs, and error budgets; and map risk profiles to business outcomes.'
          },
          {
            step: 2,
            title: 'Observability & Alerting Foundation',
            description: 'Instrument key services with OpenTelemetry, Prometheus, and Grafana; standardize alerting using golden signals; and create centralized SRE dashboards.'
          },
          {
            step: 3,
            title: 'Release Engineering & GitOps',
            description: 'Re-architect release workflows with ArgoCD, Argo Rollouts, and Helm; enable progressive delivery; and set up CI/CD gates and test automation.'
          },
          {
            step: 4,
            title: 'Resilience Design & Scaling',
            description: 'Implement auto-scaling, pod disruption budgets, and fault tolerance across AZs; use KEDA/HPA/VPA for elasticity; and perform chaos testing.'
          },
          {
            step: 5,
            title: 'Incident Response & Reliability Automation',
            description: 'Automate incident response with Slack & PagerDuty; document runbooks and RCA workflows; and conduct game days and SRE ceremonies.'
          }
        ]
      }       
    ]
  },
  industryUseCases: {
    title: 'Industries & Use Cases We Power with SRE Expertise',
    subtitle: 'Our production-tested Site Reliability Engineering solutions enable performance, availability, and cost control across critical industries.',
    useCases: [
      {
        icon: '🌐',
        title: 'SaaS Platforms',
        description: 'Ensure high availability across multiple regions with failover strategies, traffic routing, and zero-downtime deployments.',
        features: [
          'Multi-region high availability',
          'Failover and traffic routing',
          'Zero-downtime rollouts'
        ]
      },
      {
        icon: '🧠',
        title: 'ML Workloads',
        description: 'Enable GPU fleet autoscaling, enforce SLA-driven data pipelines, and reduce model training failures via robust observability.',
        features: [
          'GPU autoscaling',
          'Data pipeline SLA enforcement',
          'Model training reliability'
        ]
      },
      {
        icon: '🛍️',
        title: 'E-commerce Platforms',
        description: 'Optimize for low latency, handle seasonal load like Black Friday, and reduce cart abandonment with resilient APIs and performance tuning.',
        features: [
          'Low-latency optimization',
          'Black Friday scaling strategies',
          'Cart abandonment reduction'
        ]
      },
      {
        icon: '💳',
        title: 'Fintech Applications',
        description: 'Achieve regulatory-grade uptime, maintain detailed incident traceability, and integrate secure observability pipelines.',
        features: [
          'Regulatory uptime guarantees',
          'Incident traceability',
          'Secure observability'
        ]
      }
    ]
  }
  
};

// Prometheus and Grafana Consulting
const prometheusGrafanaService: CompleteServiceData = {
  hero: {
    icon: '📊',
    category: 'Monitoring',
    title: 'Prometheus and Grafana Consulting',
    subtitle: 'Advanced Monitoring & Observability',
    description: 'Turnkey observability platform with real-time metrics, advanced alerting, and rich dashboards for proactive troubleshooting.',
    highlights: ['High-Scale Deployments', 'SLO Management', 'Long-Term Storage', 'Multi-Cloud Monitoring']
  },
  content: {
    sections: [
      {
        title: 'Why Betaque for Prometheus & Grafana',
        type: 'benefits',
        content: [
          {
            icon: '📈',
            title: 'Proven High-Scale Deployments',
            description: 'Built and operated Prometheus federation across clusters handling 40B+ API hits per month.',
            features: ['40B+ API hits handled', 'Low-latency ingestion', 'Sub-second dashboards']
          },
          {
            icon: '🌐',
            title: 'Cloud-Native & Multi-Cloud Expertise',
            description: 'Implement agent-based metric collection across EKS, AKS, GKE, or bare-metal Kubernetes.',
            features: ['Multi-cloud support', 'BYOC deployment', 'Consistent instrumentation']
          }
        ]
      },
      {
        title: 'Core Prometheus & Grafana Stack Offerings',
        type: 'cards',
        content: [
          {
            title: '📊 Monitoring Architecture Design & Deployment',
            description: 'Design scalable and resilient Prometheus setups with long-term storage, automated provisioning, and centralized Grafana dashboards for multi-tenant visibility.',
            features: [
              'Prometheus federation & remote-write to Cortex/Thanos/Mimir',
              'Scalable TSDB with object storage and compaction tuning',
              'Helm/Terraform-based provisioning of exporters and operators',
              'SSO-enabled multi-tenant Grafana deployments with prebuilt dashboards'
            ]
          },
          {
            title: '🚨 Advanced Alerting & SLO Management',
            description: 'Set up highly available Alertmanager clusters with intelligent routing, suppression logic, and Grafana-based SLO dashboards to track error budgets and service health.',
            features: [
              'HA Alertmanager with custom routing trees',
              'CI-integrated alert silence automation',
              'Grafana SLO dashboards with burn rate & threshold insights',
              'Alert integrations with PagerDuty, Opsgenie, Slack'
            ]
          },
          {
            title: '📦 Kubernetes-Specific Monitoring & Troubleshooting',
            description: 'Enable deep observability into Kubernetes clusters with automated pod metrics scraping, autoscaling insights, and correlation across metrics, logs, and traces.',
            features: [
              'ServiceMonitor-based pod and control-plane metrics collection',
              'Cluster autoscaling dashboards (HPA & resource usage)',
              'Grafana Explore: integrated logs, metrics, and traces',
              'OpenTelemetry and EFK/ELK stack integration'
            ]
          },
          {
            title: '🛠️ Managed Monitoring & Ongoing Support',
            description: 'Get end-to-end monitoring coverage with 24/7 support, proactive health checks, upgrade management, and custom Grafana visualizations tailored to your KPIs.',
            features: [
              'Target health checks and WAL/compaction monitors',
              'TSDB retention and query latency optimization',
              'Safe upgrade rollouts with backup & validation',
              'Custom Grafana plugins and reusable dashboard templates'
            ]
          }
        ]
      },
      {
        title: 'Expertise in High-Scale, Multi-Regional Monitoring',
        type: 'cards',
        content: [
          {
            title: '🌐 Federated Metric Aggregation Across Regions',
            description: 'Architect multi-region Prometheus with Thanos Querier to enable unified global queries while retaining region-specific data sovereignty and retention.',
            features: [
              'Prometheus Federv2 regional clusters',
              'Thanos Querier for global data aggregation',
              'Seamless multi-day, multi-region metric queries'
            ]
          },
          {
            title: '🚦 Anycast Ingress & Global Alert Routing',
            description: 'Ensure global availability and alert reliability with DNS Anycast or cloud-native ingress plus regional fallback webhooks to handle alert delivery even during outages.',
            features: [
              'DNS Anycast or global load balancer for ingestion endpoints',
              'Region-based alert routing with failover receivers',
              'High resilience for alert delivery across failure domains'
            ]
          },
          {
            title: '🧯 Disaster Recovery & Cross-Cluster Failover',
            description: 'Enable automatic failover and data continuity by redirecting remote-write traffic and Grafana dashboards to secondary Prometheus clusters during region-level outages.',
            features: [
              'Cross-region Prometheus failover setup',
              'Auto-switching Grafana data sources',
              'Continuous observability during regional disasters'
            ]
          },
          {
            title: '☁️ Multi-Cloud Metric Harmonization',
            description: 'Normalize metrics across AWS, GCP, and Azure to create unified dashboards showing combined service performance across cloud providers.',
            features: [
              'Cross-cloud metric name and label normalization',
              'Unified dashboards across AWS, GCP, and Azure',
              'Support for VM, Kubernetes, and managed service metrics'
            ]
          }
        ]
      },
      {
        title: 'Why Partner with Us for Prometheus & Grafana Stack Consulting?',
        type: 'benefits',
        content: [
          {
            icon: '📈',
            title: 'End-to-End Visibility',
            description: 'Unified dashboards across infrastructure, clusters, and applications accelerate root cause analysis.',
            features: [
              'Infrastructure, cluster, and app metrics in one place',
              'Faster root cause identification',
              'Customizable visualizations'
            ]
          },
          {
            icon: '🚨',
            title: 'Rapid Alerting & Precise Escalation',
            description: 'Smart alerting routes only actionable incidents to the right on-call engineers.',
            features: [
              'Sophisticated alerting rules',
              'Dynamic routing logic',
              'Reduced alert fatigue'
            ]
          },
          {
            icon: '💾',
            title: 'Cost-Optimized Storage',
            description: 'Tiered time-series storage balances performance with long-term cost savings.',
            features: [
              'Hot/warm/cold TSDB storage architecture',
              'Optimized S3/GCS costs',
              'Fast queries for recent metrics'
            ]
          },
          {
            icon: '🌍',
            title: 'Seamless Multi-Cloud Operations',
            description: 'Monitor environments across any cloud or on-prem with consistent patterns.',
            features: [
              'Works with EKS, AKS, GKE, and on-prem Kubernetes',
              'Avoids vendor lock-in',
              'Unified cross-cloud observability'
            ]
          },
          {
            icon: '🔐',
            title: 'Security & Compliance Assurance',
            description: 'Built-in security practices meet industry compliance standards effortlessly.',
            features: [
              'TLS-encrypted metric scrapes',
              'Ephemeral RBAC tokens for exporters',
              'Drift detection for config compliance'
            ]
          },
          {
            icon: '⚡',
            title: 'Developer & SRE Efficiency',
            description: 'Automated workflows minimize manual effort and boost engineering productivity.',
            features: [
              'Helm-based upgrades',
              'GitOps for dashboard versioning',
              'Automated provisioning of monitoring stack'
            ]
          }
        ]
      }                  
    ]
  }
};

// Policy-as-Code Services
const policyAsCodeService: CompleteServiceData = {
  hero: {
    icon: '📋',
    category: 'Governance',
    title: 'Policy-as-Code Services',
    subtitle: 'Automated Governance & Compliance',
    description: 'Implement comprehensive policy automation across your infrastructure, Kubernetes, CI/CD, and application delivery pipelines.',
    highlights: ['Multi-Layer Coverage', 'Automation-First', 'Compliance Integration', 'Real-Time Enforcement']
  },
  content: {
    sections: [
      {
        title: 'Multi-Layered Policy Coverage',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Infrastructure-as-Code (Terraform/Terragrunt)',
            description: 'Use OPA with Conftest and Checkov to enforce guardrails across Terraform modules.'
          },
          {
            step: 2,
            title: 'Kubernetes Platform Policies',
            description: 'Real-time policy validation using Kyverno, OPA/Gatekeeper, and Kubewarden.'
          },
          {
            step: 3,
            title: 'CI/CD Pipeline Guardrails',
            description: 'Integrated policy engines ensure code cannot ship without satisfying pre-deployment gates.'
          },
          {
            step: 4,
            title: 'Application Layer & Runtime',
            description: 'Input validation via Rego policies and runtime verification hooks.'
          }
        ]
      }
    ]
  },
  additionalSections: [
    {
      type: 'rich-text',
      title: '🔐 Aligning Policy-as-Code (PaC) with Your Engineering Workflows',
      content: `Betaque doesn’t just install PaC tooling — we align it with your engineering workflows. Whether you're a startup enforcing hygiene across repos, or an enterprise seeking PCI/GDPR/HIPAA readiness, we integrate compliance at the code level. We implement GitOps-based governance pipelines that scale across dev teams, multi-cloud IaC repos, and Kubernetes clusters. Our proven track record with startups and unicorns ensures you get real policy enforcement — not shelfware.`
    }
  ]
};

// GitOps Consulting & Implementation Services
const gitopsService: CompleteServiceData = {
  hero: {
    icon: '🔄',
    category: 'DevOps',
    title: 'GitOps Implementation Services',
    subtitle: 'Build Reliable, Auditable, and Fast Software Delivery with GitOps',
    description: 'Modernize Kubernetes delivery workflows using GitOps principles for predictability, automation, and visibility.',
    highlights: ['Progressive Delivery', 'Multi-Cluster Management', 'Security-First', 'Automated Rollback']
  },
  content: {
    sections: [
      {
        title: 'Why Partner with a GitOps Consulting Expert?',
        type: 'benefits',
        content: [
          {
            icon: '📝',
            title: 'Declarative Everything',
            description: 'Define infrastructure, applications, environments, and policies declaratively for reproducible environments.',
            features: ['Version-controlled environments', 'Reproducible deployments', 'Scale with your team']
          },
          {
            icon: '👩‍💻',
            title: 'Developer Experience & Flow',
            description: 'Free developers from manual scripts and enable safe, quick, independent deployment.',
            features: ['Eliminate manual scripts', 'Independent deployments', 'Consistent staging']
          },
          {
            icon: '🚀',
            title: 'Faster, Safer Releases',
            description: 'Deliver changes to production several times a day using Git-based workflows and progressive delivery.',
            features: ['Multiple daily deployments', 'Progressive delivery', 'Automated validation']
          }
        ]
      },
      {
        title: 'What We Offer as GitOps Consulting & Implementation Experts',
        type: 'benefits',
        content: [
          {
            icon: '🧭',
            title: 'GitOps Advisory & Strategy Services',
            description: 'Consulting and roadmap planning to transition teams from CI/CD to GitOps with tailored adoption blueprints.',
            features: [
              'Infrastructure maturity assessment',
              'Custom GitOps adoption blueprints',
              'Tool selection and Git structure guidance',
              'Automation and security alignment'
            ]
          },
          {
            icon: '⚙️',
            title: 'GitOps Implementation – From Plan to Production',
            description: 'Build or migrate GitOps pipelines using Argo CD, FluxCD, Helm, and Kustomize for declarative, automated workflows.',
            features: [
              'Git-backed deployment pipelines',
              'Drift detection and rollback support',
              'Infrastructure-as-code and secure secrets integration',
              'Cluster lifecycle & app delivery automation'
            ]
          },
          {
            icon: '🔧',
            title: 'Enterprise-Grade GitOps Support',
            description: 'Support multi-cluster environments with real-time monitoring, incident response, and scaling assistance.',
            features: [
              'Multi-cluster ArgoCD ApplicationSets management',
              'Real-time GitOps monitoring',
              'Incident response and tool upgrades',
              'Cross-region GitOps scaling'
            ]
          },
          {
            icon: '🎓',
            title: 'GitOps Enablement & Training',
            description: 'Workshops and enablement sessions to make teams self-sufficient in GitOps fundamentals and tools.',
            features: [
              'Pull-based deployment model training',
              'Drift correction and progressive rollout',
              'Hands-on tool workshops: Flux, Helm, Terraform, Argo Rollouts',
              'Team enablement for independent releases'
            ]
          }
        ]
      },
      {
        title: 'Comprehensive GitOps Consulting Offerings',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'GitOps Strategy & Roadmap',
            description: 'Evaluate delivery architecture and design tailored GitOps strategy aligned with product goals, compliance, and team structure; decisions on mono-repo vs multi-repo, promotion workflows, and branch strategies.'
          },
          {
            step: 2,
            title: 'Toolchain Implementation & CI Integration',
            description: 'Implement GitOps pipelines with Argo CD, FluxCD, Helm, Kustomize, and Harness; integrate with GitHub, GitLab, Bitbucket, and CI tools like GitHub Actions, Jenkins, CircleCI to automate builds, tests, and deployments.'
          },
          {
            step: 3,
            title: 'Progressive Delivery with Argo Rollouts',
            description: 'Enable canary, blue-green, and experimental rollouts with traffic shifting, metric analysis (Prometheus, Datadog), automatic rollback; support trunk-based development for fast, auditable production releases.'
          },
          {
            step: 4,
            title: 'GitOps Security & Compliance',
            description: 'Embed security best practices: encrypted secrets with Vault, Secret Manager, SOPS; dynamic secrets injection; enforce RBAC and SSO via OIDC; implement approval gates and policy-as-code with OPA/Gatekeeper to align with SOC2, HIPAA, ISO27001.'
          },
          {
            step: 5,
            title: 'Multi-Cluster, Multi-Region Deployment',
            description: 'Orchestrate Git-driven deployments across regions and clouds using Argo CD ApplicationSets, GKE Fleet, Terraform+GitOps; provide unified control plane, service discovery, config consistency, and compliance across clusters.'
          },
          {
            step: 6,
            title: 'Enablement, Dashboards & Training',
            description: 'Deliver Slack-integrated dashboards, real-time rollout monitors, Kubecost integration, and structured training to empower teams for autonomous GitOps pipeline ownership.'
          },
          {
            step: 7,
            title: 'Infrastructure GitOps',
            description: 'Extend GitOps to infrastructure management via Terraform workflows using Atlantis, Terragrunt, Terramate; enable cloud infra changes through PRs, enforce plan-and-apply reviews, automate drift detection, and manage multi-cloud infra securely.'
          }
        ]
      },
      {
        title: 'Supported Tools & Ecosystem',
        type: 'tools',
        tools: [
          {
            category: 'GitOps Engines',
            items: ['Argo CD', 'FluxCD', 'Harness'],
            description: 'Automate and manage Kubernetes deployments with reliable GitOps engines for continuous delivery and synchronization.',
            icon: '🔄'
          },
          {
            category: 'Templating & Config Management',
            items: ['Helm', 'Kustomize', 'Jsonnet', 'KubeVela'],
            description: 'Simplify Kubernetes app configuration and management using powerful templating and configuration tools.',
            icon: '📝'
          },
          {
            category: 'Version Control Systems',
            items: ['GitHub', 'GitLab', 'Bitbucket'],
            description: 'Maintain your codebase with trusted version control platforms enabling collaboration and source management.',
            icon: '📁'
          },
          {
            category: 'CI Integration',
            items: ['GitHub Actions', 'GitLab CI/CD', 'Jenkins', 'CircleCI', 'Drone CI', 'Bitbucket Pipelines'],
            description: 'Integrate continuous integration pipelines to automate testing and build processes seamlessly.',
            icon: '⚙️'
          },
          {
            category: 'Progressive Delivery',
            items: ['Argo Rollouts', 'Flagger', 'Spinnaker'],
            description: 'Implement canary releases, blue-green deployments, and other progressive delivery strategies for safer rollouts.',
            icon: '🚦'
          },
          {
            category: 'Infrastructure as Code',
            items: ['Terraform', 'Terragrunt', 'Atlantis', 'Pulumi'],
            description: 'Provision and manage infrastructure declaratively to ensure reproducible and scalable environments.',
            icon: '🏗️'
          },
          {
            category: 'Secrets Management',
            items: ['HashiCorp Vault', 'SOPS', 'Sealed Secrets', 'AWS Secret Manager', 'GCP Secret Manager', 'Azure Secret Manager', 'External Secrets Operator'],
            description: 'Securely store, manage, and inject secrets and sensitive configuration across your applications and infrastructure.',
            icon: '🔐'
          },
          {
            category: 'Security & Policy Enforcement',
            items: ['OPA/Gatekeeper', 'Kyverno', 'Rego', 'K-Rail', 'Polaris'],
            description: 'Enforce security policies and compliance through policy-as-code frameworks integrated with your workflows.',
            icon: '🛡️'
          },
          {
            category: 'Drift Detection & Auditing',
            items: ['Argo CD drift sync', 'Terraform Driftctl', 'Git audit logs'],
            description: 'Detect configuration drift and maintain audit trails to ensure infrastructure consistency and compliance.',
            icon: '🔍'
          }
        ]
      }            
    ]
  }
};

// Infrastructure Design & Delivery
const infrastructureDesignService: CompleteServiceData = {
  hero: {
    icon: '🏛️',
    category: 'Architecture',
    title: 'Infrastructure Design & Delivery',
    subtitle: 'Architected for Least Cost, Maximum Security & Reliability',
    description: 'Build infrastructure that\'s scalable, secure, and ready for growth with cloud-native, serverless, and automation-first platforms.',
    highlights: ['Cloud-Native Design', 'Security Built-In', 'Scalability Focus', 'End-to-End Implementation']
  },
  content: {
    sections: [
      {
        title: 'What We Offer',
        type: 'cards',
        content: [
          {
            title: 'Cloud-Native & Serverless Infrastructure Architecture',
            description: 'Design infrastructure that fits your application architecture using microservices, serverless, and event-driven systems.',
            features: ['Microservices ready', 'Serverless patterns', 'Event-driven systems']
          },
          {
            title: 'Secure, Compliant & Resilient Foundations',
            description: 'Security and compliance embedded from day zero with least privilege IAM, VPC segmentation, and encryption.',
            features: ['SOC2, ISO27001, HIPAA', 'Least privilege IAM', 'Encryption by default']
          },
          {
            title: 'Kubernetes Platform Engineering',
            description: 'Single-region to multi-region clusters with GitOps, progressive delivery, observability, and cost-aware scheduling.',
            features: ['Multi-region clusters', 'GitOps integration', 'Cost optimization']
          }
        ]
      },
      {
        title: 'Supported Platforms & Tools',
        type: 'tools',
        tools: [
          {
            category: 'Cloud',
            items: ['AWS', 'GCP', 'Azure', 'Hetzner'],
            description: 'Deploy scalable and secure infrastructure across leading public and private cloud providers tailored to your needs.',
            icon: '☁️'
          },
          {
            category: 'Infrastructure as Code (IaC)',
            items: ['Terraform', 'Terragrunt', 'Pulumi', 'Ansible'],
            description: 'Automate infrastructure provisioning and management using modern IaC tools for consistent and repeatable environments.',
            icon: '🏗️'
          },
          {
            category: 'CI/CD',
            items: ['GitHub Actions', 'GitLab CI', 'Jenkins', 'CircleCI', 'Argo Workflows'],
            description: 'Integrate powerful CI/CD pipelines to streamline development workflows and accelerate delivery.',
            icon: '🔄'
          },
          {
            category: 'Kubernetes',
            items: ['GKE', 'EKS', 'AKS', 'K3s', 'Rancher'],
            description: 'Manage containerized workloads with ease using robust Kubernetes platforms for both enterprise and edge deployments.',
            icon: '☸️'
          },
          {
            category: 'GitOps',
            items: ['ArgoCD', 'Flux', 'Helm', 'Kustomize'],
            description: 'Achieve declarative, version-controlled deployments with GitOps tools for scalable and reliable operations.',
            icon: '🚀'
          },
          {
            category: 'Secrets Management',
            items: ['HashiCorp Vault', 'AWS Secrets Manager', 'External Secrets'],
            description: 'Securely manage sensitive credentials and configurations with centralized secrets management solutions.',
            icon: '🔐'
          },
          {
            category: 'Monitoring & Logs',
            items: ['Prometheus', 'Grafana', 'Loki', 'ELK', 'OpenTelemetry'],
            description: 'Get end-to-end observability and insights with monitoring, logging, and tracing tools across your systems.',
            icon: '📊'
          },
          {
            category: 'Security',
            items: ['OPA/Gatekeeper', 'Kyverno', 'IAM', 'VPC SC', 'KMS', 'SIEM'],
            description: 'Ensure compliance and protect your infrastructure with identity, access, and policy enforcement tools.',
            icon: '🛡️'
          },
          {
            category: 'Serverless',
            items: ['AWS Lambda', 'Step Functions', 'GCP Cloud Functions', 'EventBridge', 'Pub/Sub'],
            description: 'Build scalable, event-driven applications using fully managed serverless platforms with minimal operational overhead.',
            icon: '⚡'
          }
        ]
      },
      {
        title: 'Action Plan to Launch Infrastructure from Scratch',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Assessment & Blueprinting',
            description: 'Deep dive into product architecture, business goals, compliance mandates, and expected user growth.'
          },
          {
            step: 2,
            title: 'Infrastructure Design',
            description: 'Define service mesh, VPC layout, subnetting strategy, scaling policies, and cloud platform mix (AWS, GCP, Azure).'
          },
          {
            step: 3,
            title: 'IaC + GitOps Implementation',
            description: 'Implement modular Terraform/Terragrunt setups, CI/CD pipelines, and GitOps integration for infrastructure and application deployments.'
          },
          {
            step: 4,
            title: 'Security Hardening & Compliance Setup',
            description: 'Set up least privilege IAM, secret rotation, audit logging, and pre-configure for SOC2, HIPAA, and ISO27001 compliance.'
          },
          {
            step: 5,
            title: 'Observability, Chaos Readiness & Cost Guardrails',
            description: 'Integrate Prometheus monitoring, distributed tracing, budget alerts, autoscaling policies, and conduct simulated outages for resilience.'
          },
          {
            step: 6,
            title: 'Documentation, Automation & Year 2 Readiness',
            description: 'Provide complete runbooks, onboarding guides, and workflows to maintain future compliance with minimal overhead, including CI/CD workflows, disaster recovery drills, and cost/access matrix.'
          }
        ]
      }
            
    ]
  },
  additionalSections: [
    {
      type: 'rich-text',
      title: '🚀 Let’s Build Your Infrastructure Right the First Time',
      content: `Let Betaque help you ship with confidence — from a compliant foundation to resilient day-2 operations. Our builds are engineered for reliability, security, and scale.
  
  Schedule a discovery call today.`
    }
  ],  
};

// Cloud Migration Services
const cloudMigrationService: CompleteServiceData = {
  hero: {
    icon: '☁️',
    category: 'Migration',
    title: 'Cloud Migration Services',
    subtitle: 'Seamless Cloud Migration & Modernization',
    description: 'Comprehensive, end-to-end solution for moving workloads smoothly and securely to cloud environments with minimal downtime.',
    highlights: ['Zero-Downtime Strategies', 'Multi-Cloud Approaches', 'Legacy Modernization', 'Cost Optimization']
  },
  content: {
    sections: [
      {
        title: 'Why Betaque for Cloud Migration Services?',
        type: 'benefits',
        content: [
          {
            icon: '🎓',
            title: 'Deep Technical Expertise and Certifications',
            description: 'Our team holds AWS, GCP, and Azure certifications with large-scale migration experience.',
            features: ['Multi-cloud certified', 'Enterprise experience', 'Best practices adherence']
          },
          {
            icon: '🤖',
            title: 'Automation-First Methodology',
            description: 'Prebuilt, premium modules and templates that streamline migration pipelines.',
            features: ['Battle-tested modules', 'Reproducible workflows', 'Reduced manual steps']
          },
          {
            icon: '📋',
            title: 'Comprehensive Assessment and Planning',
            description: 'Stakeholder interviews, architecture reviews, and detailed migration roadmaps.',
            features: ['RTO/RPO targets', 'Interdependency mapping', 'Rollback strategies']
          }
        ]
      },
      {
        title: 'Cloud Migration Service Offerings',
        type: 'cards',
        content: [
          {
            title: 'On-Premises to Public Cloud or BYOC Deployment',
            description: 'Migrate workloads from on-premises data centers to public clouds with our BYOC model.',
            features: ['Data sovereignty preserved', 'Managed services deployment', 'Minimal disruption']
          },
          {
            title: 'Inter-Cloud Platform Migration',
            description: 'Transition workloads between cloud providers with service parity mapping and automated transformations.',
            features: ['Service parity mapping', 'Automated transformations', 'Zero config drift']
          },
          {
            title: 'VM-to-Container Transformation',
            description: 'Convert legacy applications to containerized microservices with Kubernetes orchestration.',
            features: ['Container optimization', 'Microservices refactoring', 'Auto-scaling policies']
          }
        ]
      }
    ]
  },
  faq: {
    title: 'Migration FAQ',
    faqs: [
      {
        question: 'What could go wrong during a migration, and how does Betaque prevent downtime or data loss?',
        answer: 'Risks include unexpected downtime, data inconsistency, and compatibility issues. Our structured approach features phased cutovers, automated data replication with CDC, and rollback plans to ensure workloads stay online.'
      },
      {
        question: 'How long will it take to complete my migration?',
        answer: 'Timeline depends on complexity, data volume, and compliance requirements. Simple lift-and-shift can finish in weeks, while refactoring takes months. We provide detailed roadmaps with Discovery, PoC, Pilot, and Production phases.'
      }
    ]
  }
};

// Cloud Compliance & Security Readiness Services
const complianceSecurityService: CompleteServiceData = {
  hero: {
    icon: '🛡️',
    category: 'Security',
    title: 'Cloud Compliance & Security Services',
    subtitle: 'End-to-End Compliance Readiness — Without Third-Party SaaS',
    description: 'Become audit-ready for SOC 2, HIPAA, GDPR, ISO 27001, and PCI-DSS without relying on expensive SaaS solutions.',
    highlights: ['SOC 2, ISO 27001, HIPAA', 'No SaaS Dependencies', 'Automated Monitoring', 'Security-First']
  },
  content: {
    sections: [
      {
        title: 'What We Offer',
        type: 'cards',
        content: [
          {
            title: 'Infrastructure Hardening & Access Control',
            description: 'Translate compliance controls into secure infrastructure deployments using Terraform, AWS IAM, and Kubernetes RBAC.',
            features: ['Encryption by default', 'Robust logging', 'Multi-cloud policies']
          },
          {
            title: 'Internal Processes & Evidence Collection',
            description: 'DevSecOps-first approach that automates evidence generation through CI/CD logs and Git histories.',
            features: ['Automated evidence', 'SDLC policies', 'Change approval workflows']
          },
          {
            title: 'Documentation & Policy Automation',
            description: 'Version-controlled policies in Git with enforcement integrated directly into engineering workflows.',
            features: ['Git-based policies', 'Training schedules', 'Auto-review cadences']
          }
        ]
      }
    ]
  }
};

// CI/CD Services
const cicdService: CompleteServiceData = {
  hero: {
    icon: '🔄',
    category: 'DevOps',
    title: 'CI/CD Services',
    subtitle: 'Advanced Continuous Integration & Deployment',
    description: 'Robust, end-to-end automation framework that accelerates software delivery, data integration, and ML model deployment.',
    highlights: ['Multi-Tool Expertise', 'Security Integration', 'Automated Testing', 'GitOps Integration']
  },
  content: {
    sections: [
      {
        title: 'Expertise in Major CI/CD Tools',
        type: 'tools',
        tools: [
          {
            category: 'Primary Platforms',
            items: ['GitHub Actions', 'Jenkins & Jenkins X', 'GitLab CI/CD', 'CircleCI & Travis CI']
          },
          {
            category: 'Cloud-Native',
            items: ['Azure DevOps', 'AWS CodePipeline', 'Google Cloud Build', 'Tekton']
          },
          {
            category: 'GitOps Controllers',
            items: ['Argo CD', 'Flux', 'ArgoCD', 'Kubernetes-Native CI']
          }
        ]
      },
      {
        title: 'Why Betaque for CI/CD Services?',
        type: 'benefits',
        content: [
          {
            icon: '📊',
            title: 'Domain Expertise in Code, Data & Model Pipelines',
            description: 'Built CI/CD pipelines for application code, data artifacts, and ML models with automated validation.',
            features: ['Data provenance checks', 'Model validation', 'Multi-artifact support']
          },
          {
            icon: '🚀',
            title: 'GitOps & Progressive Delivery Mastery',
            description: 'GitOps-centric workflows with canary releases, A/B tests, and blue/green deployments.',
            features: ['Progressive delivery', 'Automated rollbacks', 'Risk reduction']
          }
        ]
      }
    ]
  }
};

// Backup & Disaster Recovery Services
const backupDrService: CompleteServiceData = {
  hero: {
    icon: '💾',
    category: 'Data Protection',
    title: 'Backup & Disaster Recovery Services',
    subtitle: 'Always Ready, Never Caught Off-Guard',
    description: 'Cloud-native, Kubernetes-aware backup and disaster recovery strategies that align with business continuity goals.',
    highlights: ['Kubernetes-Native', 'Multi-Cloud DR', 'Compliance-Ready', 'Automated Testing']
  },
  content: {
    sections: [
      {
        title: 'What We Offer',
        type: 'cards',
        content: [
          {
            title: 'Kubernetes-Aware Backup Strategies',
            description: 'Intelligent, incremental backups using Velero, Stash, and Kasten K10 for stateless and stateful apps.',
            features: ['Persistent volumes', 'CRDs and secrets', 'Full cluster states']
          },
          {
            title: 'Cloud-Native Disaster Recovery',
            description: 'Secure, incremental database backups across accounts and regions, fully encrypted.',
            features: ['Multi-region replication', 'DNS failover', 'Minutes RTO/RPO']
          },
          {
            title: 'Application-Centric DR',
            description: 'DR flows tailored to business-critical services with GitOps tooling for instant redeployment.',
            features: ['GitOps integration', 'DNS switching', 'Config drift prevention']
          }
        ]
      }
    ]
  }
};

// Vulnerability Assessment & Penetration Testing
const pentestingService: CompleteServiceData = {
  hero: {
    icon: '🔍',
    category: 'Security Testing',
    title: 'Vulnerability Assessment & Penetration Testing',
    subtitle: 'Comprehensive Security Testing & CVSS Scoring',
    description: 'Professional security testing with actionable remediation guidance following industry-standard methodologies.',
    highlights: ['OWASP Standards', 'CVSS Scoring', 'Cloud-Native Testing', 'Actionable Results']
  },
  content: {
    sections: [
      {
        title: 'Types of Assessments',
        type: 'cards',
        content: [
          {
            title: 'Black Box Assessment',
            description: 'No internal knowledge of systems - simulate external malicious actors.',
            features: ['External perspective', 'Real-world scenarios', 'Zero disruption']
          },
          {
            title: 'Gray/White Box Assessment',
            description: 'Limited to full access for comprehensive vulnerability discovery.',
            features: ['Insider threat simulation', 'Code access', 'Comprehensive coverage']
          },
          {
            title: 'Red Team Assessments',
            description: 'Full-spectrum adversary simulation with advanced persistent threat modeling.',
            features: ['Advanced threats', 'Multiple attack vectors', 'Real-world tactics']
          }
        ]
      },
      {
        title: 'Security Standards Followed',
        type: 'list',
        items: [
          'OWASP - Industry-leading application security standards and Top 10 vulnerabilities',
          'NIST - U.S. government cybersecurity frameworks and special publications',
          'PTES - Structured penetration testing methodology covering all phases',
          'CVSS - Common Vulnerability Scoring System for standardized severity ratings'
        ]
      },
      {
        title: 'Security Assessment Engagement Roadmap',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Define Scope',
            description: 'Establish the assessment’s boundaries, goals, and rules of engagement.'
          },
          {
            step: 2,
            title: 'Information Gathering',
            description: 'Collect data through reconnaissance techniques to understand the target environment.'
          },
          {
            step: 3,
            title: 'Threat Modeling',
            description: 'Identify potential threats and attack vectors relevant to the target systems.'
          },
          {
            step: 4,
            title: 'Vulnerability Identification',
            description: 'Utilize automated tools and manual methods to discover security weaknesses.'
          },
          {
            step: 5,
            title: 'Vulnerability Analysis',
            description: 'Assess the discovered vulnerabilities to determine their potential impact and exploitability.'
          },
          {
            step: 6,
            title: 'Exploitation Planning',
            description: 'Develop strategies to exploit identified vulnerabilities in a controlled manner.'
          },
          {
            step: 7,
            title: 'Exploitation',
            description: 'Attempt to exploit vulnerabilities to validate their existence and potential impact.'
          },
          {
            step: 8,
            title: 'Post-Exploitation',
            description: 'Determine the extent of access gained and the potential for lateral movement within the network.'
          },
          {
            step: 9,
            title: 'Reporting',
            description: 'Document findings, including vulnerabilities discovered, exploitation results, and recommended remediation steps.'
          },
          {
            step: 10,
            title: 'Remediation and Retesting',
            description: 'Collaborate with stakeholders to address identified issues and verify the effectiveness of implemented fixes.'
          }
        ]
      }
      
    ]
  }
};

// Export all services
export const allServicesData: { [key: string]: CompleteServiceData } = {
  'cloud-cost-optimization': cloudCostOptimizationService,
  'kubernetes-consulting': kubernetesConsultingService,
  'terraform-pulumi': terraformPulumiService,
  'sre-consulting': sreConsultingService,
  'prometheus-grafana': prometheusGrafanaService,
  'policy-as-code': policyAsCodeService,
  'gitops-consulting': gitopsService,
  'infrastructure-design': infrastructureDesignService,
  'cloud-migration': cloudMigrationService,
  'cloud-compliance': complianceSecurityService,
  'ci-cd-services': cicdService,
  'backup-disaster-recovery': backupDrService,
  'vulnerability-assessment': pentestingService
};
  